'use client';

/**
 * BackBreadcrumb — the mono-caps "← BACK TO X" link used to step out
 * of a drilled-in list view. Same recipe as the section labels
 * (JetBrains Mono, 10px / fw 700 / 0.14em / uppercase, ink).
 *
 * The arrow is a Lucide `ArrowLeft` icon at strokeWidth 2.5 (build
 * 1512, Sebastian Telegram 2026-05-26) — the previous Unicode `←`
 * glyph rendered thin in the mono font and read weak next to the bold
 * caps label. Callers MUST NOT prefix the label with their own arrow
 * (the build-1512 fix dropped `← ` from `allCategories` for exactly
 * this reason — two arrows side by side).
 *
 * Extracted in the 1394–1411 review sweep — the BY COLOR / BY BRAND /
 * BY WEIGHT / colorway breadcrumbs in /yarn were four character-for-
 * character copies, and /active had a fifth near-identical one.
 *
 * `style` overrides the wrapper for the few callers that need a
 * different padding (e.g. /active uses '4px 0' instead of '4px 4px').
 */

import type { CSSProperties, MouseEventHandler } from 'react';
import { ArrowLeft } from 'lucide-react';

export type BackBreadcrumbProps = {
  /** Text after the arrow, e.g. "ALL YARNS". Do NOT include `←`. */
  label: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  style?: CSSProperties;
};

export function BackBreadcrumb({ label, onClick, style }: BackBreadcrumbProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        alignSelf: 'flex-start',
        padding: '4px 4px',
        background: 'transparent',
        border: 'none',
        fontFamily: 'var(--font-mono)',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: 'var(--ink)',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        ...style,
      }}
    >
      <ArrowLeft size={12} strokeWidth={2.5} aria-hidden />
      {label}
    </button>
  );
}
